import React, { useState } from 'react';
import { auth } from '../services/api';
import { toast } from 'react-toastify';
import PasswordRequirements, { isPasswordValid } from './PasswordRequirements';

/**
 * Lets any signed-in user change their own password.
 *
 * The current password is required — an admin resetting someone else's
 * password goes through the admin panel instead and does not need it.
 */
const ChangePasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const meetsPolicy = isPasswordValid(newPassword);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const sameAsOld = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit = currentPassword.length > 0 && meetsPolicy && matches && !sameAsOld && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);
      await auth.changePassword({ currentPassword, newPassword, confirmPassword });
      toast.success('Password updated');
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00a884]';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Change your password</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current password
              </label>
              <input
                type={show ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={field}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New password
              </label>
              <input
                type={show ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={field}
                autoComplete="new-password"
                required
              />
              <PasswordRequirements password={newPassword} />
              {sameAsOld && (
                <p className="text-xs text-red-600 mt-1">
                  New password must be different from your current one.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repeat new password
              </label>
              <input
                type={show ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={field}
                autoComplete="new-password"
                required
              />
              {confirmPassword.length > 0 && !matches && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>
              )}
              {matches && (
                <p className="text-xs text-green-700 mt-1">Passwords match.</p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={show} onChange={() => setShow(!show)} />
              Show passwords
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`px-4 py-2 rounded-md text-white ${
                  canSubmit ? 'bg-[#00a884] hover:bg-[#008f72]' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving…' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
